import { useEffect, useRef } from "react";

/**
 * A live waveform of what the microphone is hearing.
 *
 * WHY IT IS WORTH THE CODE. The recording IS the answer in this system,
 * and until now the only feedback while dictating was a counter ticking
 * upwards -- which looks identical whether the microphone is working,
 * muted, or picking up the wrong device. A candidate in another country,
 * on their own laptop, under a clock, had no way to tell a good take
 * from four minutes of silence until they stopped and played it back.
 * A moving trace answers that continuously and without being read.
 *
 * It draws from an AnalyserNode on the SAME MediaStream the recorder is
 * muxing, so it cannot show activity the recording does not contain --
 * a waveform fed from anywhere else would be reassurance rather than
 * evidence.
 *
 * NOT A METER, and not the recording's own rendered shape. It is the
 * last second or so of amplitude, scrolling. Rendering the whole take
 * would mean holding and re-drawing the entire buffer for a report that
 * can run minutes, and would answer a question ("what did the shape of
 * minute two look like") nobody is asking mid-dictation.
 *
 * The canvas is `aria-hidden` and paired with a live region in the
 * recorder: a moving picture is not an accessible signal on its own.
 */
export function RecordingWaveform({
  stream,
  active,
}: {
  /** The live capture stream, or null when not recording. */
  stream: MediaStream | null;
  active: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (!stream || !active) return undefined;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    // A context per recording, closed on stop. Leaving one open holds
    // the audio hardware awake and, on some browsers, keeps the OS
    // recording indicator lit after the take has finished -- the same
    // complaint the recorder already guards against by stopping tracks.
    const Ctx =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!Ctx) return undefined; // no Web Audio: the counter still works
    const audio = new Ctx();
    const source = audio.createMediaStreamSource(stream);
    const analyser = audio.createAnalyser();
    // 2048 is enough resolution for a legible trace and small enough
    // that reading it every frame costs nothing.
    analyser.fftSize = 2048;
    source.connect(analyser);
    // Deliberately NOT connected to the destination: routing the
    // microphone to the speakers would give the candidate a howling
    // feedback loop the moment they are not wearing headphones.

    const buf = new Uint8Array(analyser.fftSize);
    const ctx2d = canvas.getContext("2d");
    let raf = 0;

    const draw = () => {
      raf = requestAnimationFrame(draw);
      if (!ctx2d) return;

      // Match the backing store to the CSS size and the device ratio, or
      // the trace is blurry on every laptop made in the last decade.
      const ratio = window.devicePixelRatio || 1;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (canvas.width !== w * ratio || canvas.height !== h * ratio) {
        canvas.width = w * ratio;
        canvas.height = h * ratio;
      }
      ctx2d.setTransform(ratio, 0, 0, ratio, 0, 0);
      ctx2d.clearRect(0, 0, w, h);

      analyser.getByteTimeDomainData(buf);

      // Colours read from the live tokens, so the trace follows the
      // theme instead of being a hard-coded green that vanishes in one
      // of them.
      const accent = getComputedStyle(document.documentElement)
        .getPropertyValue("--status-urgent")
        .trim();
      ctx2d.lineWidth = 2;
      ctx2d.strokeStyle = accent ? `rgb(${accent})` : "#c23a30";
      ctx2d.beginPath();

      const slice = w / buf.length;
      for (let i = 0; i < buf.length; i++) {
        // 128 is silence in this encoding; the trace is a flat line
        // through the middle when nothing is being said, which is
        // exactly the signal a candidate needs to see.
        const v = (buf[i] as number) / 128.0;
        const y = (v * h) / 2;
        const x = i * slice;
        if (i === 0) ctx2d.moveTo(x, y);
        else ctx2d.lineTo(x, y);
      }
      ctx2d.stroke();
    };
    draw();

    return () => {
      cancelAnimationFrame(raf);
      source.disconnect();
      analyser.disconnect();
      void audio.close();
    };
  }, [stream, active]);

  if (!active) return null;
  return (
    <canvas
      ref={canvasRef}
      data-cy="recording-waveform"
      aria-hidden
      className="h-10 w-full rounded-md bg-background"
    />
  );
}
